import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import HiveMapView, { MapSkeleton } from "@/components/HiveMapView";
import { Globe2, MapPin } from "lucide-react";

export default function ApiaryMapWidget() {
  const [hives, setHives] = useState(null); // null = loading

  useEffect(() => {
    api.get("/hives").then((r) => setHives(r.data || [])).catch(() => setHives([]));
  }, []);

  const pinned = useMemo(
    () => (hives || [])
      .filter((h) => h.latitude != null && h.longitude != null)
      .map((h) => ({
        id: h.id,
        name: h.name,
        color: h.color || "#EAB308",
        lat: h.latitude,
        lon: h.longitude,
        location: h.weather_location_label || h.location || "",
        strength: h.strength,
        yard: h.yard,
      })),
    [hives]
  );

  const total = hives?.length ?? 0;
  const missing = total - pinned.length;

  if (hives === null) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="apiary-map-widget">
        <MapSkeleton height={260} />
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="apiary-map-widget">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-amber-700" />
          <h2 className="font-display text-xl font-medium">My apiary map</h2>
        </div>
        <div className="text-xs text-stone-500">
          {pinned.length} of {total} hive{total === 1 ? "" : "s"} pinned
        </div>
      </div>

      {total === 0 ? (
        <div className="py-10 text-center">
          <MapPin className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500">Add your first hive to see it on the map.</p>
          <Link to="/app/hives" className="inline-block mt-3 text-sm text-amber-700 hover:underline" data-testid="apiary-map-add-hive">Add a hive →</Link>
        </div>
      ) : pinned.length === 0 ? (
        <div className="py-10 text-center bg-amber-50/60 rounded-lg border border-amber-100">
          <MapPin className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-stone-700">None of your hives have coordinates yet.</p>
          <p className="text-xs text-stone-500 mt-1">Edit a hive and search its city or use the GPS button to pin it.</p>
          <Link to="/app/hives" className="inline-block mt-3 text-sm text-amber-700 hover:underline" data-testid="apiary-map-edit-hives">Pin a hive →</Link>
        </div>
      ) : (
        <>
          <HiveMapView hives={pinned} height={260} linkable LinkComponent={Link} />
          {missing > 0 && (
            <p className="text-xs text-stone-500 mt-3">
              {missing} hive{missing === 1 ? "" : "s"} not on the map yet — add a location to plot {missing === 1 ? "it" : "them"}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
